import { createLogger, serializeError } from 'logging';

import type {
    ImageToVideoRequest,
    ImageToVideoTaskCreateResponse,
    ImageToVideoTaskStatusResponse,
    TextToVideoRequest,
    TextToVideoAlgoParams,
    ImageToVideoAlgoParams,
    TextToVideoTaskCreateResponse,
    TextToVideoTaskStatusResponse,
    TextToImageRequest,
    TextToImageAlgoParams,
    TextToImageTaskCreateResponse,
    TextToImageTaskStatusResponse,
} from '@videoblocks/ml-text-service-client';

import { contentEditApi } from './mlTextServiceConfig';

const logger = createLogger('VideoGenerationApi');

// ============================================================================
// Re-export generated types for direct consumer access
// Per dsellers-sb's feedback: expose generated types to reduce transformation layers
// ============================================================================
export type {
    ImageToVideoTaskCreateResponse,
    ImageToVideoTaskStatusResponse,
    TextToVideoTaskCreateResponse,
    TextToVideoTaskStatusResponse,
    ImageToVideoRequest,
    ImageToVideoAlgoParams,
    TextToVideoRequest,
    TextToVideoAlgoParams,
    TextToImageRequest,
    TextToImageAlgoParams,
    TextToImageTaskCreateResponse,
    TextToImageTaskStatusResponse,
};

// ============================================================================
// Helper types for better type safety (generated types use `any` in some places)
// ============================================================================

export type RunwayTaskStatus = 'CANCELLED' | 'FAILED' | 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'THROTTLED';

// Model and ratio types for algo_params
export type RunwayTextToVideoModelId = 'veo3.1' | 'veo3.1_fast' | 'veo3';
export type RunwayImageToVideoModelId = 'veo3.1' | 'veo3.1_fast' | 'veo3' | 'gen4_turbo' | 'gen3a_turbo';
export type RunwayRatio = '1280:720' | '720:1280' | '1080:1920' | '1920:1080';

export type TaskStatusResponse = TextToVideoTaskStatusResponse | ImageToVideoTaskStatusResponse;

// ============================================================================
// Text-to-Video
// ============================================================================

/**
 * Creates a new Text-to-Video task.
 *
 * @param request - The text-to-video request parameters (uses generated type directly)
 * @returns Task creation response with task_id, or null on error
 */
const createTextToVideoTask = async (request: TextToVideoRequest): Promise<TextToVideoTaskCreateResponse | null> => {
    try {
        const response = await contentEditApi.contentEditTextToVideoTasksPost({
            textToVideoRequest: request,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in createTextToVideoTask');
        return null;
    }
};

/**
 * Gets the status of a Text-to-Video task.
 *
 * @param taskId - The task UUID
 * @returns Task status response with runway_task_status and output_url when complete
 */
const getTextToVideoTaskStatus = async (taskId: string): Promise<TextToVideoTaskStatusResponse | null> => {
    try {
        const response = await contentEditApi.contentEditTextToVideoTasksTaskIdGet({
            taskId,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in getTextToVideoTaskStatus');
        return null;
    }
};

// ============================================================================
// Image-to-Video
// ============================================================================

/**
 * Creates a new Image-to-Video task.
 *
 * @param request - The image-to-video request parameters
 * @returns Task creation response with task_id, or null on error
 */
const createImageToVideoTask = async (request: ImageToVideoRequest): Promise<ImageToVideoTaskCreateResponse | null> => {
    try {
        const response = await contentEditApi.contentEditImageToVideoTasksPost({
            imageToVideoRequest: request,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in createImageToVideoTask');
        return null;
    }
};

/**
 * Gets the status of an Image-to-Video task.
 *
 * @param taskId - The task UUID
 * @returns Task status response with runway_task_status and output_url when complete
 */
const getImageToVideoTaskStatus = async (taskId: string): Promise<ImageToVideoTaskStatusResponse | null> => {
    try {
        const response = await contentEditApi.contentEditImageToVideoTasksTaskIdGet({
            taskId,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in getImageToVideoTaskStatus');
        return null;
    }
};

// ============================================================================
// Text-to-Image
// ============================================================================

/**
 * Creates a new Text-to-Image task.
 *
 * @param request - The text-to-image request parameters
 * @returns Task creation response with task_id, or null on error
 */
const createTextToImageTask = async (request: TextToImageRequest): Promise<TextToImageTaskCreateResponse | null> => {
    try {
        const response = await contentEditApi.contentEditTextToImageTasksPost({
            textToImageRequest: request,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in createTextToImageTask');
        return null;
    }
};

/**
 * Gets the status of a Text-to-Image task.
 *
 * @param taskId - The task UUID
 * @returns Task status response with runway_task_status and output_url when complete
 */
const getTextToImageTaskStatus = async (taskId: string): Promise<TextToImageTaskStatusResponse | null> => {
    try {
        const response = await contentEditApi.contentEditTextToImageTasksTaskIdGet({
            taskId,
        });

        return response.data;
    } catch (error) {
        logger.error({ error: serializeError(error as Error) }, 'Error in getTextToImageTaskStatus');
        return null;
    }
};

// ============================================================================
// Polling Helper
// ============================================================================

export interface PollOptions {
    maxAttempts?: number;
    intervalMs?: number;
    onStatusUpdate?: (status: TaskStatusResponse) => void;
}

const TERMINAL_STATUSES: RunwayTaskStatus[] = ['SUCCEEDED', 'FAILED', 'CANCELLED'];

/**
 * Polls a task until it reaches a terminal status.
 *
 * @param getStatusFn - Function to get task status
 * @param options - Polling options
 * @returns Final task status response
 */
const pollTaskUntilComplete = async (
    getStatusFn: () => Promise<TaskStatusResponse | null>,
    options: PollOptions = {},
): Promise<TaskStatusResponse | null> => {
    const { maxAttempts = 120, intervalMs = 5000, onStatusUpdate } = options;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const status = await getStatusFn();

        if (!status) {
            logger.error({ attempt }, 'Failed to get task status during polling');
            return null;
        }

        onStatusUpdate?.(status);

        if (TERMINAL_STATUSES.includes(status.results.runway_task_status)) {
            return status;
        }

        // Wait before next poll
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    logger.error({ maxAttempts }, 'Task polling timed out');
    return null;
};

/**
 * Creates a text-to-video task and polls until completion.
 *
 * @param request - The text-to-video request parameters
 * @param pollOptions - Optional polling configuration
 * @returns Final task status with output_url on success
 */
const textToVideo = async (
    request: TextToVideoRequest,
    pollOptions?: PollOptions,
): Promise<TextToVideoTaskStatusResponse | null> => {
    const createResponse = await createTextToVideoTask(request);
    if (!createResponse?.results?.task_id) {
        return null;
    }

    const taskId = createResponse.results.task_id;
    return pollTaskUntilComplete(() => getTextToVideoTaskStatus(taskId), pollOptions);
};

/**
 * Creates an image-to-video task and polls until completion.
 *
 * @param request - The image-to-video request parameters
 * @param pollOptions - Optional polling configuration
 * @returns Final task status with output_url on success
 */
const imageToVideo = async (
    request: ImageToVideoRequest,
    pollOptions?: PollOptions,
): Promise<ImageToVideoTaskStatusResponse | null> => {
    const createResponse = await createImageToVideoTask(request);
    if (!createResponse?.results?.task_id) {
        return null;
    }

    const taskId = createResponse.results.task_id;
    return pollTaskUntilComplete(() => getImageToVideoTaskStatus(taskId), pollOptions);
};

export const VideoGenerationApi = {
    // Task creation
    createTextToVideoTask,
    createImageToVideoTask,
    createTextToImageTask,
    // Task status
    getTextToVideoTaskStatus,
    getImageToVideoTaskStatus,
    getTextToImageTaskStatus,
    // Convenience methods with polling
    textToVideo,
    imageToVideo,
    // Polling helper
    pollTaskUntilComplete,
};
